import { useState } from "react";
import { toast } from "sonner";
import { Facebook, Linkedin, Mail, Link as LinkIcon, Share2, Check } from "lucide-react";
import { API } from "../lib/api";

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.31-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.49-8.41" />
  </svg>
);

const buildLinks = (url, text) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  return [
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { id: "x", label: "X", icon: XIcon, href: `https://x.com/intent/post?url=${u}&text=${t}` },
    { id: "whatsapp", label: "WhatsApp", icon: WhatsAppIcon, href: `https://wa.me/?text=${t}%20${u}` },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { id: "email", label: "Email", icon: Mail, href: `mailto:?subject=${t}&body=${t}%0A${u}` },
  ];
};

const btn = "inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors";

export const ShareButtons = ({ slug, name, title = "Share this profile", shareUrl: url, copyUrl, text: shareText, only, testPrefix = "share" }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || `${API}/share/${slug}`;
  const text = shareText || `${name} – TV & film extra on extras.tv`;

  const copy = async () => {
    await navigator.clipboard.writeText(copyUrl || `${window.location.origin}/${slug}`);
    setCopied(true);
    toast.success("Link copied — paste it into Instagram, TikTok or anywhere");
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = () => navigator.share({ title: text, url: shareUrl }).catch(() => {});

  return (
    <div className={title ? "mt-6" : ""} data-testid={`${testPrefix}-buttons`}>
      {title && <p className="label-xs">{title}</p>}
      <div className={`flex flex-wrap gap-2 ${title ? "mt-3" : ""}`}>
        {buildLinks(shareUrl, text).filter((l) => !only || only.includes(l.id)).map((l) => (
          <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" className={btn} data-testid={`${testPrefix}-${l.id}`}>
            <l.icon size={16} /> {l.label}
          </a>
        ))}
        <button onClick={copy} className={btn} data-testid={`${testPrefix}-copy-link`}>
          {copied ? <Check size={16} /> : <LinkIcon size={16} />} {copied ? "Copied" : "Copy link"}
        </button>
        {!only && typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativeShare} className={btn} data-testid={`${testPrefix}-native`}>
            <Share2 size={16} /> More…
          </button>
        )}
      </div>
    </div>
  );
};
